import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { InlineCaption, InlineElementContainer } from '../atoms/Atoms.tsx'
import {
  ScrollstoryDocumentDataBodyImageSlice,
  ScrollstoryDocumentDataBodyImageSliceItem,
  ScrollstoryDocumentDataBodyImageSlicePrimary,
} from '../../../types.generated'
import {
  VERTICAL_ELEMENT_MARGIN_BOTTOM_DESKTOP,
  VERTICAL_ELEMENT_MARGIN_BOTTOM_MOBILE,
} from '../../theme/theme'
import { BREAKPOINT_MOBILE, useIsPortrait } from '../../util/breakpoints'
import { asText, RichTextField } from '@prismicio/client'
import { LazyImage } from '../atoms/LazyImage.tsx'
import isPropValid from '@emotion/is-prop-valid'
import { PrismicRichText } from '@prismicio/react'
import { VIEWPORT_HEIGHT } from '../../util/viewportHeight.ts'

export interface InlineImageProps {
  image: ScrollstoryDocumentDataBodyImageSlice
}
const OversizedImageLoadingContainer = styled.div`
  width: 100%;
  height: ${VIEWPORT_HEIGHT};
  display: flex;
  justify-content: center;
  align-items: center;

  margin-bottom: ${VERTICAL_ELEMENT_MARGIN_BOTTOM_DESKTOP};
  @media ${BREAKPOINT_MOBILE} {
    margin-bottom: ${VERTICAL_ELEMENT_MARGIN_BOTTOM_MOBILE};
  }
`

const OversizedImage = styled(LazyImage, {
  shouldForwardProp: (prop) => isPropValid(prop),
})<{ isLoaded: boolean }>`
  width: 100%;
  height: auto;
  display: ${({ isLoaded }) => (isLoaded ? 'block' : 'none')};

  margin-bottom: ${VERTICAL_ELEMENT_MARGIN_BOTTOM_DESKTOP};
  @media ${BREAKPOINT_MOBILE} {
    margin-bottom: ${VERTICAL_ELEMENT_MARGIN_BOTTOM_MOBILE};
  }
`

const ImageContainer = styled.div`
  position: relative;
`

const InlineImageContainer = styled(ImageContainer)`
  display: flex;
  flex: 1 0 auto;
  margin-bottom: 0.5em;
`

const InlineImg = styled(LazyImage)`
  width: 100%;
`

const getImageUrl = (
  image: ScrollstoryDocumentDataBodyImageSlicePrimary,
  isPortrait: boolean
): string => {
  if (isPortrait && image.image_portrait.url) {
    return image.image_portrait.url
  }

  return image.image.url ?? ''
}

const getAltText = (
  image: ScrollstoryDocumentDataBodyImageSlicePrimary,
  isPortrait: boolean
): string => {
  if (isPortrait && image.image_portrait.url) {
    return image.image_portrait.alt ?? ''
  }

  return image.image.alt ?? ''
}

export default function InlineImage(props: InlineImageProps) {
  const [isLoaded, setIsLoaded] = useState(false)
  const [optimizedImageUrl, setOptimizedImageUrl] = useState('')

  const isPortrait = useIsPortrait()

  const image = props.image.primary

  const imageUrl = getImageUrl(image, isPortrait)
  const altText = getAltText(image, isPortrait)

  const pins = props.image.items.map((item, index) => {
    return (
      <ImagePin
        key={index}
        isPortrait={isPortrait}
        baseCss={image.pin_shared_css}
        {...item}
      />
    )
  })

  useEffect(() => {
    setOptimizedImageUrl(`${imageUrl}?fit=clip&w=${window.innerWidth}`)
  }, [imageUrl])

  if (image.image_type === 'oversized') {
    return (
      <>
        {!isLoaded && <OversizedImageLoadingContainer />}
        {optimizedImageUrl && (
          <ImageContainer>
            <PinsContainer>{pins}</PinsContainer>
            <OversizedImage
              isLoaded={isLoaded}
              src={optimizedImageUrl}
              alt={altText}
              onLoad={() => setIsLoaded(true)}
              data-cy="oversizedImage"
            />
          </ImageContainer>
        )}
      </>
    )
  } else {
    const caption = asText(image.media_caption)
    const source = asText(image.media_source)

    return (
      <InlineElementContainer data-cy="inlineImageContainer">
        <InlineImageContainer>
          <PinsContainer>{pins}</PinsContainer>
          <InlineImg src={imageUrl} alt={altText} />
        </InlineImageContainer>

        <InlineCaption>
          {caption} <i>{source}</i>
        </InlineCaption>
      </InlineElementContainer>
    )
  }
}

const PinsContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
`

interface ImagePinProps extends ScrollstoryDocumentDataBodyImageSliceItem {
  isPortrait: boolean
  baseCss: RichTextField
}

function ImagePin(props: ImagePinProps) {
  const specificCss = props.isPortrait
    ? props.pin_css_portrait
    : props.pin_css_landscape
  const baseCss = props.baseCss

  return (
    <Pin baseCss={baseCss} specificCss={specificCss}>
      <PrismicRichText field={props.pin_text} />
    </Pin>
  )
}

const Pin = styled.div<{ baseCss: RichTextField; specificCss: RichTextField }>`
  position: absolute;
  ${({ baseCss }) => asText(baseCss) ?? ''}
  ${({ specificCss }) => asText(specificCss) ?? ''}
`
